import React, { useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  Clock3,
  ExternalLink,
  Loader2,
  MessageCircle,
  Package,
  Phone,
  Store,
  UserRoundCheck,
} from 'lucide-react';
import {
  apiErrorMessage,
  reviewPreDispatchDelivery,
} from './operationalIntelligenceApi';

const READINESS = {
  HIGH_ATTENTION: {
    label: 'ALTA ATENCIÓN',
    badge: 'border-red-300 bg-red-100 text-red-800',
    accent: 'border-l-red-500',
  },
  REVIEW_RECOMMENDED: {
    label: 'REVISIÓN RECOMENDADA',
    badge: 'border-orange-300 bg-orange-100 text-orange-800',
    accent: 'border-l-orange-500',
  },
  ELEVATED: {
    label: 'ELEVADO',
    badge: 'border-amber-300 bg-amber-100 text-amber-800',
    accent: 'border-l-amber-500',
  },
  WAITING_RESPONSE: {
    label: 'ESPERANDO RESPUESTA',
    badge: 'border-blue-300 bg-blue-100 text-blue-800',
    accent: 'border-l-blue-500',
  },
  NORMAL: {
    label: 'NORMAL',
    badge: 'border-emerald-300 bg-emerald-100 text-emerald-800',
    accent: 'border-l-emerald-500',
  },
};

const COMMUNICATION_LABELS = {
  CONTACT_PENDING: 'CONTACTO PENDIENTE',
  WAITING_RESPONSE: 'ESPERANDO RESPUESTA',
  CUSTOMER_REPLIED: 'CLIENTE RESPONDIÓ',
  NO_REPLY: 'SIN RESPUESTA',
};

const REASON_LABELS = {
  NO_CUSTOMER_REPLY: 'Sin respuesta',
  WAITING_RESPONSE_WINDOW: 'Dentro de ventana',
  PRIOR_RECIPIENT_CANCEL: 'Cancelación previa',
  MULTIPLE_PRIOR_RECIPIENT_CANCELS: 'Múltiples cancelaciones previas',
  ACCOUNT_HIGH_CANCELLATION: 'Cuenta ≥ 50%',
  ACCOUNT_ELEVATED_CANCELLATION: 'Cancelación de cuenta elevada',
  ACCOUNT_CANCELLATION_EVIDENCE: 'Cancelación de cuenta con muestra baja',
  ACCOUNT_LOW_CONFIDENCE: 'Muestra baja',
  CUSTOMER_REPLIED: 'Cliente respondió',
  CUSTOMER_CONTACT_PENDING: 'Contacto pendiente',
  PRIOR_RECIPIENT_REJECTION_CONTEXT: 'Rechazo previo (contexto)',
};

const PACKAGE_LABELS = {
  in_warehouse: 'En almacén',
  in_fastmax: 'En Fast Max',
  mixed: 'Paquetes en etapas distintas',
};

const DECISION_LABELS = {
  contacted: 'Segundo contacto realizado',
  confirmed: 'Cliente confirmado',
  continue: 'Continuar despacho',
  dismissed: 'Descartar alerta',
};

const CHANNEL_LABELS = {
  phone: 'Llamada telefónica',
  whatsapp: 'WhatsApp',
  fastmax_chat: 'Fast Max Chat',
  in_person: 'Presencial',
  other: 'Otro',
};

function formatDate(value) {
  if (!value) return 'Sin fecha';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
}

function elapsedLabel(minutes) {
  if (minutes == null) return 'Sin contacto saliente';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `${hours} h ${remainder} min` : `${hours} h`;
}

function reviewLabel(review) {
  if (!review?.decision) return null;
  return DECISION_LABELS[review.decision] || review.decision;
}

export default function PreDispatchDeliveryRow({ item, onOpenContext, onReviewed }) {
  const [reviewOpen, setReviewOpen] = useState(false);
  const [decision, setDecision] = useState('contacted');
  const [confirmationChannel, setConfirmationChannel] = useState('phone');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const delivery = item.delivery || {};
  const communication = item.communication || {};
  const recipient = item.recipient_history || {};
  const account = item.account_history || {};
  const readiness = item.readiness || {};
  const visual = READINESS[readiness.level] || READINESS.NORMAL;
  const latestReviewLabel = reviewLabel(item.review);

  async function submitReview(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const result = await reviewPreDispatchDelivery(item.delivery_point_id, {
        decision,
        ...(decision === 'confirmed' ? { confirmation_channel: confirmationChannel } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setReviewOpen(false);
      setNote('');
      await onReviewed?.(result);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo registrar la revisión'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <article className={`overflow-hidden rounded-2xl border border-l-4 border-slate-200 bg-white shadow-sm ${visual.accent}`}>
      <div className="grid gap-5 p-5 xl:grid-cols-[minmax(230px,1.15fr)_minmax(190px,.8fr)_minmax(215px,.9fr)_minmax(210px,.9fr)] xl:p-6">
        <section className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-3 py-1 text-xs font-black tracking-wide ${visual.badge}`}>
              {visual.label}
            </span>
            <span className="text-xs font-extrabold uppercase tracking-[0.16em] text-slate-400">
              #{item.delivery_point_id}
            </span>
          </div>
          <h3 className="mt-3 truncate text-xl font-black text-slate-900">
            {delivery.recipient_name || 'Destinatario sin nombre'}
          </h3>
          <p className="mt-1 flex items-center gap-2 font-mono text-sm font-bold text-slate-600">
            <Phone className="h-4 w-4 text-slate-400" />
            {delivery.recipient_phone || 'Sin teléfono'}
          </p>
          <div className="mt-3 flex items-center gap-2 text-sm font-extrabold text-brand-primary">
            <Store className="h-4 w-4" />
            {delivery.seller_username || account.username || 'Sin cuenta asignada'}
          </div>
          <p className="mt-2 text-xs font-semibold text-slate-500">
            {formatDate(delivery.delivery_date)} · Slot {delivery.slot ?? 'sin asignar'} · {delivery.district || 'Sin distrito'}
          </p>
        </section>

        <section className="rounded-xl bg-slate-50 p-4">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-slate-500">
            <MessageCircle className="h-4 w-4" /> Comunicación
          </p>
          <p className={`mt-3 text-sm font-black ${communication.state === 'NO_REPLY' ? 'text-red-700' : 'text-slate-800'}`}>
            {COMMUNICATION_LABELS[communication.state] || communication.state || 'SIN DATOS'}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-600">
            <Clock3 className="h-4 w-4" />
            {elapsedLabel(communication.minutes_since_outbound)}
          </p>
          <div className="mt-4 border-t border-slate-200 pt-3">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-slate-500">
              <Package className="h-4 w-4" /> Etapa del paquete
            </p>
            <p className="mt-1 text-sm font-bold text-slate-800">
              {delivery.package_count
                ? PACKAGE_LABELS[delivery.package_status] || delivery.package_status
                : 'Sin paquete · etapa temprana'}
            </p>
          </div>
        </section>

        <section className="space-y-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-slate-400">Destinatario</p>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-sm">
              <span className="font-bold text-emerald-700">{recipient.prior_delivered ?? recipient.delivered ?? 0} entregadas</span>
              <span className="font-bold text-slate-800">{recipient.prior_cancelled ?? recipient.cancelled ?? 0} canceladas</span>
              <span className="font-semibold text-red-700">{recipient.prior_rejected ?? recipient.rejected ?? 0} rechazadas</span>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-4">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-slate-400">Cuenta · 90 días</p>
            <p className="mt-1 text-2xl font-black text-slate-900">{Number(account.cancellation_rate_pct || 0).toFixed(1)}%</p>
            <p className="text-sm font-semibold text-slate-600">
              cancelación · {account.terminal_attempts || 0} intentos
            </p>
            <span className={`mt-2 inline-flex rounded-full px-2.5 py-1 text-[11px] font-extrabold ${account.confidence === 'LOW' ? 'bg-slate-200 text-slate-700' : 'bg-blue-100 text-blue-700'}`}>
              CONFIANZA {account.confidence || 'LOW'}
            </span>
          </div>
        </section>

        <section className="flex min-w-0 flex-col">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-slate-400">Evidencia</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(readiness.reason_codes || []).map((reason) => (
              <span key={reason} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-bold text-slate-700">
                {REASON_LABELS[reason] || reason.replaceAll('_', ' ')}
              </span>
            ))}
          </div>
          <p className="mt-4 text-sm font-bold leading-relaxed text-slate-800">
            {readiness.recommendation}
          </p>
          {latestReviewLabel && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <span><strong>Última revisión:</strong> {latestReviewLabel}</span>
            </div>
          )}
          <div className="mt-auto flex flex-col gap-2 pt-5 sm:flex-row xl:flex-col 2xl:flex-row">
            <button
              type="button"
              onClick={() => onOpenContext(item)}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-brand-primary px-4 py-2.5 text-xs font-extrabold text-white hover:bg-pink-700"
            >
              <ExternalLink className="h-4 w-4" />
              ABRIR CONTEXTO
            </button>
            <button
              type="button"
              onClick={() => setReviewOpen((value) => !value)}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-300 px-4 py-2.5 text-xs font-extrabold text-slate-700 hover:bg-slate-50"
              aria-expanded={reviewOpen}
            >
              <UserRoundCheck className="h-4 w-4" />
              REVISAR
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${reviewOpen ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </section>
      </div>

      {reviewOpen && (
        <form onSubmit={submitReview} className="border-t border-slate-200 bg-slate-50 px-5 py-4 xl:px-6">
          <div className="grid gap-3 lg:grid-cols-[minmax(220px,.8fr)_minmax(220px,.8fr)_minmax(280px,1.4fr)_auto] lg:items-end">
            <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
              Decisión humana
              <select
                value={decision}
                onChange={(event) => setDecision(event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold normal-case tracking-normal text-slate-800 outline-none focus:border-brand-primary"
              >
                <option value="contacted">Segundo contacto realizado</option>
                <option value="confirmed">Cliente confirmado</option>
                <option value="continue">Continuar despacho</option>
                <option value="dismissed">Descartar alerta</option>
              </select>
            </label>

            {decision === 'confirmed' ? (
              <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
                Canal de confirmación
                <select
                  value={confirmationChannel}
                  onChange={(event) => setConfirmationChannel(event.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-semibold normal-case tracking-normal text-slate-800 outline-none focus:border-brand-primary"
                >
                  {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
            ) : <div className="hidden lg:block" />}

            <label className="text-xs font-extrabold uppercase tracking-wide text-slate-500">
              Nota opcional
              <input
                value={note}
                onChange={(event) => setNote(event.target.value.slice(0, 500))}
                placeholder="Contexto operativo breve, sin PII innecesaria"
                className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm font-medium normal-case tracking-normal text-slate-800 outline-none placeholder:text-slate-400 focus:border-brand-primary"
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-extrabold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Registrar
            </button>
          </div>
          {error && <p className="mt-3 text-sm font-semibold text-red-700">{error}</p>}
          <p className="mt-3 text-xs text-slate-500">
            Esta revisión solo registra evidencia y decisión humana. No cancela, posterga, bloquea ni envía mensajes automáticamente.
          </p>
        </form>
      )}
    </article>
  );
}
